import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from 'reactstrap';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { FaEye, FaCheck, FaTimes, FaExternalLinkAlt, FaExclamationTriangle } from 'react-icons/fa';

const ApprovalQueue = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  useEffect(() => {
    fetchPendingApprovals();

    const interval = setInterval(fetchPendingApprovals, 60000);
    return () => clearInterval(interval);

  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axiosPrivate.get('/madiscovery/pending');
      setPendingApprovals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      setLoading(false);
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApproval(approval);
    setShowDetailModal(true);
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;

    setProcessingId(selectedApproval.id);
    try {
      await axiosPrivate.post(`/madiscovery/approve/${selectedApproval.id}`, {
        approvedBy: 'Admin',
        notes: approvalNotes,
        targetCompanyId: selectedApproval.existingMA?.targetId || selectedApproval.possibleDuplicateCompanyId,
        acquirerCompanyId: selectedApproval.existingMA?.acquirerId,
      });

      enqueueSnackbar('M&A transaction approved and added to database!', { variant: 'success' });
      setShowApproveModal(false);
      setShowDetailModal(false);
      setApprovalNotes('');
      fetchPendingApprovals();
    } catch (error) {
      enqueueSnackbar('Failed to approve: ' + error.message, { variant: 'error' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval || !rejectReason.trim()) {
      enqueueSnackbar('Please provide a reason for rejection', { variant: 'warning' });
      return;
    }

    setProcessingId(selectedApproval.id);
    try {
      await axiosPrivate.post(`/madiscovery/reject/${selectedApproval.id}`, {
        rejectedBy: 'Admin',
        reason: rejectReason,
      });

      enqueueSnackbar('M&A suggestion rejected', { variant: 'success' });
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectReason('');
      fetchPendingApprovals();
    } catch (error) {
      enqueueSnackbar('Failed to reject: ' + error.message, { variant: 'error' });
    } finally {
      setProcessingId(null);
    }
  };

  const getConfidenceBadge = (score) => {
    if (score >= 90) return <Badge color="success">High ({score}%)</Badge>;
    if (score >= 70) return <Badge color="warning">Medium ({score}%)</Badge>;
    return <Badge color="danger">Low ({score}%)</Badge>;
  };

  const getDuplicateWarning = (approval) => {
    if (!approval.possibleDuplicateCompanyId) return null;

    const matchScore = approval.fuzzyMatchScore || 0;
    const severity = matchScore >= 95 ? 'danger' : 'warning';

    return (
      <Alert color={severity} className="mb-0">
        <FaExclamationTriangle className="me-2" />
        <strong>Possible Duplicate:</strong> {approval.possibleDuplicateCompany?.foundCompName || 'Unknown'} ({matchScore.toFixed(1)}% match)
      </Alert>
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading pending approvals...</p>
      </div>
    );
  }

  return (
    <div className="approval-queue">
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">🤖 AI-Discovered M&A Transactions</h4>
              <small className="text-muted">Pending Admin Approval</small>
            </div>
            <div>
              <Badge color="primary" pill style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}>
                {pendingApprovals.length} Pending
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {pendingApprovals.length === 0 ? (
            <Alert color="info">
              <strong>No pending approvals</strong>
              <p className="mb-0">The AI agent hasn't discovered any new M&A transactions yet. Run the agent manually or wait for the next scheduled run.</p>
            </Alert>
          ) : (
            <Table responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Acquirer</th>
                  <th>Target</th>
                  <th>Deal Value</th>
                  <th>Date</th>
                  <th>Source</th>
                  <th>AI Confidence</th>
                  <th>Duplicate Check</th>
                  <th>Discovered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.map((approval) => {
                  const isInvalid = approval.acquirerCompanyName === 'null' || !approval.acquirerCompanyName ||
                                    approval.targetCompanyName === 'null' || !approval.targetCompanyName;

                  return (
                  <tr key={approval.id} className={isInvalid ? 'table-danger' : ''}>
                    <td>
                      <strong>{approval.acquirerCompanyName}</strong>
                      {isInvalid && approval.acquirerCompanyName === 'null' && (
                        <Badge color="danger" className="ms-2">INVALID</Badge>
                      )}
                    </td>
                    <td>
                      <strong>{approval.targetCompanyName}</strong>
                      {approval.foundingYear && (
                        <div className="text-muted small">Founded: {approval.foundingYear}</div>
                      )}
                      {isInvalid && approval.targetCompanyName === 'null' && (
                        <Badge color="danger" className="ms-2">INVALID</Badge>
                      )}
                    </td>
                    <td>
                      {approval.dealValueUSD ? (
                        <span className="text-success">
                          ${(approval.dealValueUSD / 1000000).toFixed(0)}M
                        </span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>{new Date(approval.acquisitionDate).toLocaleDateString()}</td>
                    <td>
                      <Badge color="info">{approval.sourceType.replace('_', ' ')}</Badge>
                    </td>
                    <td>{getConfidenceBadge(approval.aiConfidenceScore)}</td>
                    <td>
                      {approval.existingMA?.alreadyExists ? (
                        <Badge color="danger" title="This M&A already exists in database!">
                          ❌ DUPLICATE M&A
                        </Badge>
                      ) : approval.matchedCompany ? (
                        <Badge color="warning" title={`Matched with existing company: ${approval.matchedCompany.name} (ID: ${approval.matchedCompany.id})`}>
                          ⚠️ {approval.fuzzyMatchScore?.toFixed(0)}% MATCH
                        </Badge>
                      ) : (
                        <Badge color="success">✓ NEW</Badge>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(approval.createdByAgent).toLocaleString()}
                      </small>
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleViewDetails(approval)}
                          title="View Details"
                        >
                          <FaEye />
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowApproveModal(true);
                          }}
                          disabled={processingId === approval.id}
                          title="Approve"
                        >
                          <FaCheck />
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowRejectModal(true);
                          }}
                          disabled={processingId === approval.id}
                          title="Reject"
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} toggle={() => setShowDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setShowDetailModal(false)}>
          M&A Transaction Details
        </ModalHeader>
        <ModalBody>
          {selectedApproval && (
            <>
              <h5>Extracted Information</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <th width="30%">Acquirer</th>
                    <td>{selectedApproval.acquirerCompanyName}</td>
                  </tr>
                  <tr>
                    <th>Target</th>
                    <td>{selectedApproval.targetCompanyName}</td>
                  </tr>
                  <tr>
                    <th>Deal Value</th>
                    <td>
                      {selectedApproval.dealValueUSD
                        ? `$${selectedApproval.dealValueUSD.toLocaleString()} USD`
                        : 'Not available'}
                    </td>
                  </tr>
                  <tr>
                    <th>Acquisition Date</th>
                    <td>{new Date(selectedApproval.acquisitionDate).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <th>Target Founded</th>
                    <td>{selectedApproval.foundingYear || 'Not available'}</td>
                  </tr>
                  <tr>
                    <th>Employee Count</th>
                    <td>{selectedApproval.employeeCount || 'Not available'}</td>
                  </tr>
                </tbody>
              </Table>

              <h5 className="mt-4">AI Analysis</h5>
              <p>
                <strong>Confidence Score:</strong> {getConfidenceBadge(selectedApproval.aiConfidenceScore)}
              </p>
              <p>
                <strong>Extraction Method:</strong> {selectedApproval.extractionMethod || 'OpenAI GPT-4o-mini'}
              </p>

              <h5 className="mt-4">Source Article</h5>
              <p>
                <strong>Source:</strong> {selectedApproval.sourceType.replace('_', ' ')}
                <br />
                <a href={selectedApproval.sourceUrl} target="_blank" rel="noopener noreferrer">
                  <FaExternalLinkAlt className="me-1" />
                  Open Source URL
                </a>
              </p>

              {selectedApproval.extractedText && (
                <div className="border p-3" style={{ maxHeight: '200px', overflow: 'auto', backgroundColor: '#f8f9fa' }}>
                  <small>{selectedApproval.extractedText}</small>
                </div>
              )}

              {/* Database Check Results */}
              {selectedApproval.existingMA && (
                <div className="mt-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="px-3 py-2 border-bottom" style={{ backgroundColor: '#e9ecef' }}>
                    <strong>
                      {selectedApproval.existingMA.alreadyExists ? "⚠ DUPLICATE DETECTED" : "Database Verification"}
                    </strong>
                  </div>
                  <div className="p-3">
                    {selectedApproval.existingMA.alreadyExists ? (
                      <>
                        <p className="mb-2 text-danger">
                          <strong>This M&A transaction already exists in your database:</strong>
                        </p>
                        <table className="table table-sm table-bordered bg-white mb-2">
                          <tbody>
                            <tr>
                              <td width="30%"><strong>Target Company ID:</strong></td>
                              <td>{selectedApproval.existingMA.targetId}</td>
                            </tr>
                            <tr>
                              <td><strong>Acquirer Company ID:</strong></td>
                              <td>{selectedApproval.existingMA.acquirerId}</td>
                            </tr>
                            <tr>
                              <td><strong>Existing M&A ID:</strong></td>
                              <td>{selectedApproval.existingMA.maId}</td>
                            </tr>
                            <tr>
                              <td><strong>Date in Database:</strong></td>
                              <td>{new Date(selectedApproval.existingMA.maDate).toLocaleDateString()}</td>
                            </tr>
                          </tbody>
                        </table>
                        <div className="alert alert-danger mb-0 py-2">
                          <small><strong>⚠ Warning:</strong> Approving will create a duplicate record!</small>
                        </div>
                      </>
                    ) : (
                      <>
                        <table className="table table-sm table-bordered bg-white mb-2">
                          <thead style={{ backgroundColor: '#f1f3f5' }}>
                            <tr>
                              <th width="15%">Role</th>
                              <th width="30%">AI Extracted</th>
                              <th>Database Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Target</strong></td>
                              <td>{selectedApproval.targetCompanyName}</td>
                              <td>
                                {selectedApproval.existingMA.targetFound ? (
                                  <span>
                                    <span className="text-success me-2">✓</span>
                                    {selectedApproval.existingMA.targetName !== selectedApproval.targetCompanyName && (
                                      <><strong>{selectedApproval.existingMA.targetName}</strong> - </>
                                    )}
                                    <span className="text-muted">ID: {selectedApproval.existingMA.targetId}</span>
                                  </span>
                                ) : (
                                  <span className="text-warning">⊗ Not found - will create new company</span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td><strong>Acquirer</strong></td>
                              <td>{selectedApproval.acquirerCompanyName}</td>
                              <td>
                                {selectedApproval.existingMA.acquirerFound ? (
                                  <span>
                                    <span className="text-success me-2">✓</span>
                                    {selectedApproval.existingMA.acquirerName !== selectedApproval.acquirerCompanyName && (
                                      <><strong>{selectedApproval.existingMA.acquirerName}</strong> - </>
                                    )}
                                    <span className="text-muted">ID: {selectedApproval.existingMA.acquirerId}</span>
                                  </span>
                                ) : (
                                  <span className="text-warning">⊗ Not found - will create new company</span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {(selectedApproval.existingMA.targetFound || selectedApproval.existingMA.acquirerFound) && (
                          <div className="alert alert-light border mb-0 py-2">
                            <small className="text-muted">
                              <strong>Note:</strong> Existing companies will be reused. A new M&A transaction will be created linking these companies.
                            </small>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={() => {
              setShowDetailModal(false);
              setShowApproveModal(true);
            }}
            disabled={selectedApproval?.existingMA?.alreadyExists}
          >
            <FaCheck className="me-2" />
            {selectedApproval?.existingMA?.alreadyExists ? "Cannot Approve (Duplicate)" : "Approve"}
          </Button>
          <Button
            color="danger"
            onClick={() => {
              setShowDetailModal(false);
              setShowRejectModal(true);
            }}
          >
            <FaTimes className="me-2" />
            Reject
          </Button>
          <Button color="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal isOpen={showApproveModal} toggle={() => setShowApproveModal(false)}>
        <ModalHeader toggle={() => setShowApproveModal(false)}>
          Confirm Approval
        </ModalHeader>
        <ModalBody>
          {selectedApproval && (
            <>
              <Alert color="success">
                <strong>You are about to approve:</strong>
                <br />
                {selectedApproval.acquirerCompanyName} → {selectedApproval.targetCompanyName}
              </Alert>

              {selectedApproval.possibleDuplicateCompanyId && (
                <Alert color="warning">
                  <FaExclamationTriangle className="me-2" />
                  This will be merged with existing company: <strong>{selectedApproval.possibleDuplicateCompany?.foundCompName}</strong>
                </Alert>
              )}

              <Form>
                <FormGroup>
                  <Label for="approvalNotes">Notes (optional)</Label>
                  <Input
                    type="textarea"
                    id="approvalNotes"
                    rows={3}
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add any notes about this approval..."
                  />
                </FormGroup>
              </Form>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={handleApprove}
            disabled={processingId !== null}
          >
            {processingId !== null ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                Approve & Add to Database
              </>
            )}
          </Button>
          <Button color="secondary" onClick={() => setShowApproveModal(false)} disabled={processingId !== null}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal isOpen={showRejectModal} toggle={() => setShowRejectModal(false)}>
        <ModalHeader toggle={() => setShowRejectModal(false)}>
          Reject M&A Suggestion
        </ModalHeader>
        <ModalBody>
          {selectedApproval && (
            <>
              <Alert color="danger">
                <strong>You are about to reject:</strong>
                <br />
                {selectedApproval.acquirerCompanyName} → {selectedApproval.targetCompanyName}
              </Alert>

              <Form>
                <FormGroup>
                  <Label for="rejectReason">Reason for Rejection *</Label>
                  <Input
                    type="select"
                    id="rejectReason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    required
                  >
                    <option value="">-- Select Reason --</option>
                    <option value="Not EDA-related">Not EDA-related</option>
                    <option value="Duplicate">Duplicate (already in database)</option>
                    <option value="Incorrect extraction">Incorrect extraction by AI</option>
                    <option value="Not a merger/acquisition">Not a merger/acquisition</option>
                    <option value="Insufficient information">Insufficient information</option>
                    <option value="Other">Other</option>
                  </Input>
                </FormGroup>
              </Form>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={handleReject}
            disabled={processingId !== null || !rejectReason}
          >
            {processingId !== null ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FaTimes className="me-2" />
                Reject
              </>
            )}
          </Button>
          <Button color="secondary" onClick={() => setShowRejectModal(false)} disabled={processingId !== null}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApprovalQueue;
